import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Typography,
  Card,
  Stack,
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Tooltip,
} from "@mui/material";
import { get } from "src/utils/networkUtils";
import { fBudgetMillions } from "src/utils/formatNumber";

const POSITIONS = ["GK", "DEF", "MID", "FWD"];
const POSITION_ORDER = { GK: 0, DEF: 1, MID: 2, FWD: 3 };

const TYPE_OPTIONS = [
  { value: "all", label: "All teams" },
  { value: "club", label: "Clubs" },
  { value: "national", label: "National teams" },
];

function errorMessage(err, fallback) {
  return err?.response?.data?.message ?? err?.message ?? fallback;
}

function getDefaultTournamentId(tournaments) {
  const worldCup = tournaments.find((t) =>
    `${t.competition_name ?? ""} ${t.short_code ?? ""}`
      .toLowerCase()
      .includes("world cup")
  );
  const active = tournaments.find((t) => t.status === "active");
  return (worldCup ?? active ?? tournaments[0])?.id ?? "";
}

function formatTournamentLabel(tournament) {
  return [
    tournament.competition_name,
    tournament.season_label,
    tournament.short_code,
  ]
    .filter(Boolean)
    .join(" - ");
}

// Goalkeepers first, then defenders, midfielders, forwards, positions we don't
// recognise, and people with no position last; then by surname.
function positionRank(player) {
  if (player.position) return POSITION_ORDER[player.position];
  return player.positionLabel ? 90 : 99;
}

function comparePlayers(a, b) {
  const byPosition = positionRank(a) - positionRank(b);
  if (byPosition !== 0) return byPosition;
  return `${a.lastName} ${a.firstName}`.localeCompare(
    `${b.lastName} ${b.firstName}`
  );
}

function PositionCounts({ positions }) {
  const parts = POSITIONS.map((p) => `${p} ${positions[p]}`);
  if (positions.unknown > 0) parts.push(`? ${positions.unknown}`);
  return (
    <Box sx={{ fontSize: 11, color: "text.secondary", whiteSpace: "nowrap" }}>
      {parts.join(" · ")}
    </Box>
  );
}

function FantasyCell({ fantasyPlayer }) {
  if (!fantasyPlayer) {
    return (
      <Box sx={{ fontSize: 12, color: "text.secondary" }}>Not in game</Box>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
      <Box sx={{ fontWeight: 600 }}>
        {fantasyPlayer.price != null ? fBudgetMillions(fantasyPlayer.price) : "-"}
      </Box>
      {fantasyPlayer.availabilityDisplay && (
        <Chip
          label={fantasyPlayer.availabilityDisplay}
          size="small"
          color="warning"
          variant="outlined"
        />
      )}
      {fantasyPlayer.hasLeft && (
        <Chip label="Left" size="small" color="error" variant="outlined" />
      )}
      {fantasyPlayer.isVisible === false && (
        <Chip label="Hidden" size="small" variant="outlined" />
      )}
    </Stack>
  );
}

export default function EcffSquadsPage() {
  // The tournament only decides which fantasy-game record (price,
  // availability) is shown in the "In game" column; it does not filter squads.
  const [tournaments, setTournaments] = useState([]);
  const [tournamentId, setTournamentId] = useState("");
  const [loadingTournaments, setLoadingTournaments] = useState(true);
  const [tournamentsError, setTournamentsError] = useState(null);

  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [includeEmpty, setIncludeEmpty] = useState(false);

  const [squads, setSquads] = useState(null);
  const [squadsLoading, setSquadsLoading] = useState(false);
  const [squadsError, setSquadsError] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [roster, setRoster] = useState(null);
  const [rosterError, setRosterError] = useState(null);
  const [showOthers, setShowOthers] = useState(false);

  // Tournaments
  useEffect(() => {
    let ignore = false;
    get("/admin/tournament-calendar?page=1&limit=100")
      .then((r) => {
        if (ignore) return;
        const list = r?.data?.tournamentCalendars ?? [];
        setTournaments(Array.isArray(list) ? list : []);
        setTournamentId(getDefaultTournamentId(list));
      })
      .catch((err) => {
        if (ignore) return;
        setTournamentsError(errorMessage(err, "Failed to load tournaments"));
      })
      .finally(() => {
        if (!ignore) setLoadingTournaments(false);
      });
    return () => {
      ignore = true;
    };
  }, []);

  // Debounce the search box
  useEffect(() => {
    const handle = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(handle);
  }, [search]);

  // Squads for the current filters. `ignore` drops responses that arrive after
  // the filters have already changed again.
  useEffect(() => {
    let ignore = false;
    setSquadsLoading(true);
    setSquadsError(null);

    const params = new URLSearchParams();
    if (type !== "all") params.set("type", type);
    if (query) params.set("search", query);
    if (includeEmpty) params.set("includeEmpty", "true");

    get(`/admin/squads?${params.toString()}`)
      .then((r) => {
        if (ignore) return;
        setSquads(r?.data?.items ?? []);
      })
      .catch((err) => {
        if (ignore) return;
        setSquads([]);
        setSquadsError(errorMessage(err, "Failed to load squads"));
      })
      .finally(() => {
        if (!ignore) setSquadsLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [type, query, includeEmpty]);

  // People linked to the selected team. Waits for the tournaments to load so
  // the default tournament is applied on the first request.
  useEffect(() => {
    if (!selectedId || loadingTournaments) {
      setRoster(null);
      setRosterError(null);
      return undefined;
    }
    let ignore = false;
    setRoster(null);
    setRosterError(null);

    const params = new URLSearchParams();
    if (tournamentId) params.set("tournamentCalendarId", tournamentId);

    get(`/admin/squads/${selectedId}/players?${params.toString()}`)
      .then((r) => {
        if (ignore) return;
        setRoster(r?.data ?? null);
      })
      .catch((err) => {
        if (ignore) return;
        setRosterError(errorMessage(err, "Failed to load the squad"));
      });
    return () => {
      ignore = true;
    };
  }, [selectedId, tournamentId, loadingTournaments]);

  const selectedSquad = useMemo(
    () => squads?.find((s) => s.id === selectedId) ?? null,
    [squads, selectedId]
  );
  const rosterContestant = roster?.contestant ?? selectedSquad;

  const otherCount = useMemo(
    () => (roster?.items ?? []).filter((p) => !p.positionLabel).length,
    [roster]
  );
  const visiblePlayers = useMemo(
    () =>
      (roster?.items ?? [])
        .filter((p) => showOthers || p.positionLabel)
        .sort(comparePlayers),
    [roster, showOthers]
  );

  return (
    <>
      <Helmet>
        <title> Squads | EngageCraft Fantasy Football Admin </title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 1 }}>
          Squads{squads !== null && !squadsError && ` (${squads.length})`}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Every club and national team with the players linked to it by the OPTA
          squads import. The "In game" column shows each player's price and
          availability in the fantasy game for the selected tournament.
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "flex-start" }}
          sx={{ mb: 2 }}
        >
          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 320 } }}>
            <InputLabel id="squads-tournament-label">Game tournament</InputLabel>
            <Select
              labelId="squads-tournament-label"
              label="Game tournament"
              value={tournamentId}
              onChange={(event) => setTournamentId(event.target.value)}
              disabled={loadingTournaments || tournaments.length === 0}
            >
              {tournaments.map((tournament) => (
                <MenuItem key={tournament.id} value={tournament.id}>
                  {formatTournamentLabel(tournament)}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Used for the "In game" column only</FormHelperText>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 180 } }}>
            <InputLabel id="squads-type-label">Show</InputLabel>
            <Select
              labelId="squads-type-label"
              label="Show"
              value={type}
              onChange={(event) => setType(event.target.value)}
            >
              {TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search by team name or code…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            inputProps={{ "aria-label": "Search squads" }}
            sx={{ flex: 1 }}
          />

          <FormControlLabel
            sx={{ whiteSpace: "nowrap", mr: 0 }}
            control={
              <Switch
                size="small"
                checked={includeEmpty}
                onChange={(event) => setIncludeEmpty(event.target.checked)}
              />
            }
            label="Include teams with no players"
          />
        </Stack>

        {tournamentsError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {tournamentsError}. Squads still load, but the "In game" column will
            be empty.
          </Alert>
        )}

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="flex-start"
        >
          {/* Left: teams */}
          <Card sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            {squadsError && (
              <Alert severity="error" sx={{ m: 2 }}>
                {squadsError}
              </Alert>
            )}
            {squads === null ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer
                sx={{
                  maxHeight: 640,
                  opacity: squadsLoading ? 0.6 : 1,
                  transition: "opacity 120ms",
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Team</TableCell>
                      <TableCell align="right">Players</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {squads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                          {squadsError
                            ? "Couldn't load squads."
                            : query || type !== "all"
                            ? "No teams match these filters."
                            : includeEmpty
                            ? "No teams found."
                            : "No team has players yet. Run a squads import on the OPTA Import page."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      squads.map((squad) => (
                        <TableRow
                          key={squad.id}
                          hover
                          selected={selectedId === squad.id}
                          onClick={() => setSelectedId(squad.id)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setSelectedId(squad.id);
                            }
                          }}
                          tabIndex={0}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                fontWeight: selectedId === squad.id ? 600 : 400,
                              }}
                            >
                              {squad.name}
                            </Box>
                            <Box sx={{ fontSize: 11, color: "text.secondary" }}>
                              {[
                                squad.code,
                                squad.isNationalTeam ? "National" : "Club",
                                squad.groupLabel
                                  ? `Group ${squad.groupLabel}`
                                  : null,
                                squad.isEliminated ? "Eliminated" : null,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </Box>
                            <PositionCounts positions={squad.positions} />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip
                              title={
                                squad.playerCount === 0
                                  ? "No players are linked to this team"
                                  : squad.otherCount > 0
                                  ? `${squad.otherCount} more with no position (likely staff), not counted`
                                  : ""
                              }
                            >
                              <Chip
                                label={squad.playerCount}
                                size="small"
                                color={
                                  squad.playerCount === 0 ? "error" : "default"
                                }
                              />
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>

          {/* Right: people linked to the selected team */}
          <Card sx={{ flex: 1.6, minWidth: 0, width: "100%" }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="subtitle1">
                {rosterContestant
                  ? rosterContestant.name
                  : "Select a team to see its squad"}
              </Typography>
              {rosterContestant && (
                <Typography variant="body2" color="text.secondary">
                  {[
                    rosterContestant.code,
                    rosterContestant.country,
                    rosterContestant.optaContestantId
                      ? `OPTA ${rosterContestant.optaContestantId}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              )}
              {roster && otherCount > 0 && (
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={
                    <Switch
                      size="small"
                      checked={showOthers}
                      onChange={(event) => setShowOthers(event.target.checked)}
                    />
                  }
                  label={`Show people with no position (${otherCount})`}
                />
              )}
            </Box>

            {rosterError && (
              <Alert severity="error" sx={{ m: 2 }}>
                {rosterError}
              </Alert>
            )}

            {!selectedId ? (
              <Box sx={{ p: 4, color: "text.secondary", textAlign: "center" }}>
                Pick a team on the left.
              </Box>
            ) : rosterError ? null : roster === null ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <CircularProgress />
              </Box>
            ) : visiblePlayers.length === 0 ? (
              <Box sx={{ p: 4, color: "text.secondary", textAlign: "center" }}>
                {roster.items.length === 0
                  ? "No players are linked to this team."
                  : "No players. Turn on “Show people with no position” to see the rest."}
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 640 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Pos</TableCell>
                      <TableCell>Player</TableCell>
                      <TableCell>Nationality</TableCell>
                      <TableCell>DoB</TableCell>
                      <TableCell>In game</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visiblePlayers.map((player) => (
                      <TableRow key={player.personId} hover>
                        <TableCell>
                          {player.position ?? player.positionLabel ?? "-"}
                        </TableCell>
                        <TableCell>
                          {player.firstName} {player.lastName}
                          <Box
                            sx={{
                              fontSize: 10,
                              color: "text.secondary",
                              fontFamily: "monospace",
                            }}
                          >
                            {player.optaPersonId ?? "-"}
                          </Box>
                        </TableCell>
                        <TableCell>{player.nationality ?? "-"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {player.dateOfBirth ?? "-"}
                        </TableCell>
                        <TableCell>
                          <FantasyCell fantasyPlayer={player.fantasyPlayer} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </Stack>
      </Container>
    </>
  );
}
